"use client"
import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Button } from "../../ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs"
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar"
import { ThumbsUp, ThumbsDown, MessageSquare, Edit, Calendar } from "lucide-react"
import { Textarea } from "../../ui/textarea"
import { Input } from "../../ui/input"
import { Label } from "../../ui/label"
import useTimeline from "../../../hooks/useTimeline"

const TimelineModule = ({ actionId, action }) => {
  console.log("TimelineModule rendered with actionId:", actionId)

  const {
    timeline,
    suggestions,
    comments,
    newSuggestion,
    newComment,
    activeTab,
    loading,
    setActiveTab,
    setNewComment,
    updateNewSuggestion,
    handleAddMilestone,
    handleRemoveMilestone,
    handleMilestoneChange,
    handleSubmitSuggestion,
    handleSubmitComment,
    handleVote,
    formatDate,
  } = useTimeline(actionId, action)

  console.log("TimelineModule hook data:", {
    timelineStartDate: timeline.startDate,
    timelineEndDate: timeline.endDate,
    milestonesCount: timeline.milestones?.length || 0,
    suggestionsCount: suggestions.length,
    commentsCount: comments.length,
    loading,
  })

  if (loading) {
    return <div className="p-4">Loading timeline data...</div>
  }

  return (
    <div>
      <CardHeader className="px-0 pt-0">
        <CardTitle>Timeline</CardTitle>
      </CardHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="current">Current</TabsTrigger>
          <TabsTrigger value="suggestions">Suggestions ({suggestions.length})</TabsTrigger>
          <TabsTrigger value="discussion">Discussion ({comments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="current">
          <Card>
            <CardContent className="p-4">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Start Date</h3>
                    <p className="text-lg font-medium flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                      {formatDate(timeline.startDate)}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">End Date</h3>
                    <p className="text-lg font-medium flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                      {formatDate(timeline.endDate)}
                    </p>
                  </div>
                </div>

                {timeline.milestones && timeline.milestones.length > 0 ? (
                  <div>
                    <h3 className="text-lg font-medium mb-2">Milestones</h3>
                    <div className="space-y-3">
                      {timeline.milestones.map((milestone, index) => (
                        <div key={index} className="border rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <h4 className="font-medium">{milestone.title}</h4>
                            <span className="text-sm text-gray-500">{formatDate(milestone.date)}</span>
                          </div>
                          {milestone.description && <p className="text-sm mt-1">{milestone.description}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">No milestones have been defined yet.</p>
                )}

                <div className="mt-4 flex justify-end">
                  <Button onClick={() => setActiveTab("suggestions")} variant="outline">
                    <Edit className="mr-2 h-4 w-4" /> Suggest Edit
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suggestions">
          <Card className="mb-4">
            <CardContent className="p-4">
              <h3 className="text-lg font-medium mb-4">Suggest a Timeline Update</h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Start Date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={newSuggestion.startDate}
                      onChange={(e) => updateNewSuggestion("startDate", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-date">End Date</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={newSuggestion.endDate}
                      onChange={(e) => updateNewSuggestion("endDate", e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label>Milestones</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddMilestone}>
                      Add Milestone
                    </Button>
                  </div>

                  {newSuggestion.milestones.map((milestone, index) => (
                    <div key={index} className="border rounded-lg p-3 space-y-3">
                      <div className="flex justify-between">
                        <h4 className="font-medium">Milestone {index + 1}</h4>
                        {index > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMilestone(index)}
                            className="text-red-500 h-6 w-6 p-0"
                          >
                            ×
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor={`milestone-${index}-title`} className="text-xs">
                            Title
                          </Label>
                          <Input
                            id={`milestone-${index}-title`}
                            value={milestone.title}
                            onChange={(e) => handleMilestoneChange(index, "title", e.target.value)}
                            placeholder="Milestone title"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`milestone-${index}-date`} className="text-xs">
                            Date
                          </Label>
                          <Input
                            id={`milestone-${index}-date`}
                            type="date"
                            value={milestone.date}
                            onChange={(e) => handleMilestoneChange(index, "date", e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor={`milestone-${index}-description`} className="text-xs">
                          Description
                        </Label>
                        <Textarea
                          id={`milestone-${index}-description`}
                          value={milestone.description}
                          onChange={(e) => handleMilestoneChange(index, "description", e.target.value)}
                          placeholder="Describe this milestone..."
                          className="min-h-[60px]"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSubmitSuggestion}>Submit Timeline Suggestion</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {suggestions.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No timeline suggestions yet. Be the first to suggest a timeline!
            </p>
          ) : (
            <div className="space-y-4">
              {suggestions.map((suggestion) => (
                <Card key={suggestion.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar>
                        <AvatarImage src={suggestion.user?.avatarUrl || "/placeholder.svg"} />
                        <AvatarFallback>{suggestion.user?.name?.substring(0, 2) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{suggestion.user?.name || "Anonymous"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(suggestion.createdAt).toLocaleString()}
                          </span>
                        </div>

                        <div className="space-y-3 mb-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="text-sm font-medium">Start Date:</span>
                              <p className="text-sm">{formatDate(suggestion.startDate)}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium">End Date:</span>
                              <p className="text-sm">{formatDate(suggestion.endDate)}</p>
                            </div>
                          </div>

                          {suggestion.milestones && suggestion.milestones.length > 0 && (
                            <div>
                              <span className="text-sm font-medium">Milestones:</span>
                              <div className="space-y-2 mt-1">
                                {suggestion.milestones.map((milestone, index) => (
                                  <div key={index} className="border rounded-md p-2 bg-gray-50">
                                    <div className="flex justify-between items-start">
                                      <span className="text-sm font-medium">{milestone.title}</span>
                                      <span className="text-xs text-gray-500">{formatDate(milestone.date)}</span>
                                    </div>
                                    {milestone.description && <p className="text-xs mt-1">{milestone.description}</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVote(suggestion.id, "up")}
                            className={suggestion.userVote === "up" ? "bg-green-50" : ""}
                          >
                            <ThumbsUp className="h-4 w-4 mr-1" />
                            <span>{suggestion.upvotes || 0}</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVote(suggestion.id, "down")}
                            className={suggestion.userVote === "down" ? "bg-red-50" : ""}
                          >
                            <ThumbsDown className="h-4 w-4 mr-1" />
                            <span>{suggestion.downvotes || 0}</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="discussion">
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
                {comments.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No comments yet. Start the discussion!</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3 p-3 rounded-lg bg-gray-50">
                      <Avatar>
                        <AvatarImage src={comment.user?.avatarUrl || "/placeholder.svg"} />
                        <AvatarFallback>{comment.user?.name?.substring(0, 2) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{comment.user?.name || "Anonymous"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(comment.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-2">
                <Textarea
                  placeholder="Add to the discussion..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex justify-end">
                  <Button onClick={handleSubmitComment}>
                    <MessageSquare className="mr-2 h-4 w-4" /> Post Comment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default TimelineModule
